<template>
  <div class="container-page big">
    <h2 class="title title--h2">Аналитика по сквозным задачам</h2>
    <div class="analytics-tasks">
      <!-- Filter -->
      <div class="analytics-tasks__filter">
        <ButtonComponent :class="{ active: activeItemId === 0 }" mod="border-ellipse" @click="switchFilter(0)">
          Все <span class="number">{{ totalElements }}</span>
        </ButtonComponent>
        <ButtonComponent
          v-for="btn in buttonNav"
          :key="btn.id"
          :class="{ active: activeItemId === btn.id }"
          mod="border-ellipse"
          @click="switchFilter(btn.id)"
        >
          {{ btn.text }} <span class="number">{{ btn.number }}</span>
        </ButtonComponent>
      </div>

      <div class="analytics-tasks__table">
        <div class="analytics-tasks__header">
          <div class="analytics-tasks__tr">
            <div class="analytics-tasks__th analytics-tasks__col--name">Сквозная задача</div>
            <div class="analytics-tasks__th analytics-tasks__col--countries">Целевых стран</div>
            <div class="analytics-tasks__th analytics-tasks__col--subtasks">Подзадач</div>
            <div class="analytics-tasks__th analytics-tasks__col--progress">
              Прогресс реализации подзадач по целевым странам
            </div>
            <div class="analytics-tasks__th analytics-tasks__col--problems">
              <span class="analytics-tasks__th">Подзадач с проблемами</span>
            </div>
            <div class="analytics-tasks__th analytics-tasks__col--kpi">
              <span class="analytics-tasks__th">KPI реализации сквозной задачи</span>
            </div>
          </div>
        </div>
        <div class="analytics-tasks__body">
          <div class="analytics-tasks__tr analytics-tasks__row" v-for="task in visibleTasks" :key="task.id">
            <div class="analytics-tasks__td analytics-tasks__col--name">
              <span class="analytics-tasks__name-link" @click="openDetailPage(task.id)">{{ task.name }}</span>
            </div>
            <div class="analytics-tasks__td analytics-tasks__col--countries">
              <span class="analytics-tasks__num-link" @click.stop="openModal(task, true)">
                {{ task.countries.length }}
              </span>
            </div>
            <div class="analytics-tasks__td analytics-tasks__col--subtasks">
              <span class="analytics-tasks__num-link" @click.stop="openModal(task, false)">
                {{ task.subtasks.length }}
              </span>
            </div>
            <div class="analytics-tasks__td analytics-tasks__col--progress">
              <div class="analytics-tasks__progress-top">
                <span :class="['analytics-tasks__progress-text', getProgressMod(task.kpi)]">
                  {{ task.implementedSubtasksCount }}/{{ task.subtasksCount }}
                </span>
              </div>
              <div class="analytics-tasks__progress-bar">
                <Line :percent="getProgressPercent(task)" :mod="getProgressMod(task.kpi)" />
              </div>
            </div>
            <div class="analytics-tasks__td analytics-tasks__col--problems">
              <span
                :class="[
                  'analytics-tasks__problem',
                  { 'analytics-tasks__problem--has': task.subtasksWithProblemCount > 0 },
                ]"
              >
                {{ task.subtasksWithProblemCount > 0 ? task.subtasksWithProblemCount : '–' }}
              </span>
            </div>
            <div class="analytics-tasks__td analytics-tasks__col--kpi">
              <span :class="['analytics-tasks__kpi-val', getProgressMod(task.kpi)]">
                {{ formatKpi(task.kpi) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ModalComponent
      v-model="showCountriesModal"
      title=""
      contentClass="modal--new analytics-tasks-list-modal--layout"
      :themeLigth="true"
      @close="showCountriesModal = false"
    >
      <template #header>
        <div class="modal-task__header">{{ selectedTask && selectedTask.name }}</div>
      </template>
      <div class="modal-task">
        <p class="modal-task__count">
          Целевых стран: <strong>{{ selectedTask.countries.length }}</strong>
        </p>
        <div class="modal-task__list">
          <span
            v-for="country in selectedTask.countries"
            :key="country.id"
            class="modal-task__item modal-task__item--link"
            @click="goToCountry(country.id, selectedTask.id)"
            >{{ country.name }}</span
          >
        </div>
      </div>
    </ModalComponent>

    <!-- Modal: Subtasks -->
    <ModalComponent
      v-model="showSubtasksModal"
      title=""
      contentClass="modal--new analytics-tasks-list-modal--layout"
      :themeLigth="true"
      @close="showSubtasksModal = false"
    >
      <template #header>
        <div class="modal-task__header">{{ selectedTask && selectedTask.name }}</div>
      </template>
      <div class="modal-task">
        <p class="modal-task__count">
          Подзадач: <strong>{{ selectedTask.subtasks.length }}</strong>
        </p>
        <div class="modal-task__list">
          <span v-for="(subtask, i) in selectedTask.subtasks" :key="i" class="modal-task__item">{{ subtask }}</span>
        </div>
      </div>
    </ModalComponent>
  </div>
</template>

<script>
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';

  import ModalComponent from '@/components/modals/ModalComponent.vue';

  import Line from '@/modules/crossCuttingTask/components/Line.vue';

  import API from '../api';

  export default {
    name: 'PageAnalyticsForCrossCuttingTasks',
    components: { ButtonComponent, ModalComponent, Line },
    mixins: [security],
    data() {
      return {
        activeItemId: 0,
        buttonNav: [],
        allTasksFlat: [],
        totalElements: 0,

        showCountriesModal: false,
        showSubtasksModal: false,
        selectedTask: null,
      };
    },
    created() {
      this.checkPermission(Constants.Permissions.AccessCrossCuttingTask).then((response) => {
        if (response.data) {
          API.getPageCrossCuttingTask().then((response) => {
            if (response?.data?.length) {
              this.buttonNav = response.data.map((group) => ({
                text: group.thematicBlock?.name,
                id: group.thematicBlock?.id,
                number: group.crossCuttingTasks?.length ?? 0,
              }));

              const taskMap = new Map();
              response.data.forEach((group) => this.collectTasksFromGroup(taskMap, group));

              this.allTasksFlat = [...taskMap.values()].sort((a, b) => {
                const kpiDiff = (b.kpi ?? 0) - (a.kpi ?? 0);
                if (kpiDiff !== 0) return kpiDiff;
                return String(a.name ?? '').localeCompare(String(b.name ?? ''), 'ru');
              });
              this.totalElements = this.allTasksFlat.length;
            }
          });
        } else {
          this.notAccess();
        }
      });
    },
    computed: {
      visibleTasks() {
        if (!this.activeItemId) return this.allTasksFlat;
        return this.allTasksFlat.filter((task) => task.thematicBlockIds.includes(this.activeItemId));
      },
    },
    methods: {
      switchFilter(id) {
        this.activeItemId = id;
      },

      openDetailPage(id) {
        this.$router.push({ name: 'analytics-cross-cutting-task-detail', params: { id } });
      },

      getProgressMod(kpi) {
        const val = Number(kpi);
        if (val <= 30) return 'red';
        if (val <= 80) return 'yellow';
        return 'green';
      },

      getProgressPercent(task) {
        const total = task.subtasksCount;
        if (!total) return 0;
        return (task.implementedSubtasksCount / total) * 100;
      },

      formatKpi(kpi) {
        const n = Number(kpi);
        if (isNaN(n)) return '0,0%';
        return n.toFixed(1).replace('.', ',') + '%';
      },

      openModal(task, isCountriesModal) {
        this.selectedTask = task;
        this.showCountriesModal = isCountriesModal;
        this.showSubtasksModal = !isCountriesModal;
      },

      collectTasksFromGroup(taskMap, group) {
        (group.crossCuttingTasks || []).forEach((task) => {
          if (taskMap.has(task.id)) {
            taskMap.get(task.id).thematicBlockIds.push(group.thematicBlock?.id);
          } else {
            taskMap.set(task.id, { ...task, thematicBlockIds: [group.thematicBlock?.id] });
          }
        });
      },

      goToCountry(countryId, taskId) {
        this.showCountriesModal = false;
        this.$router.push({ name: 'cross-cutting-task-card', params: { id: countryId, task: taskId } });
      },
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/theme/default/title.scss';

  .analytics-tasks {
    &__filter {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 35px;

      :deep(.button-redesign--border-ellipse) {
        margin-right: 0;
      }

      :deep(.button-redesign--border-ellipse:hover:not(.active)) {
        background: $light-grey;
        color: #000;
        border-color: $blue;

        .number {
          color: $blue;
        }
      }
    }

    &__table {
      width: 100%;
    }

    &__header {
      border-bottom: 2px solid $green;
      padding-bottom: 12px;
    }

    &__tr {
      display: flex;
      align-items: flex-start;
    }

    &__th {
      color: $dark-grey-1;
      font: $fira-14-M;
    }

    &__td {
      color: $base;
    }

    &__col--name {
      width: 42%;
      padding-right: 16px;
    }

    &__col--countries {
      width: 9%;
      margin-right: 5px;
    }

    &__col--subtasks {
      width: 9%;
      margin-right: 5px;
    }

    &__col--progress {
      width: 20%;
      padding-right: 16px;
    }

    &__col--problems {
      width: 9%;
      display: flex;
      flex-direction: column;
      align-items: center;

      .analytics-tasks__th {
        text-align: center;
      }
    }

    &__col--kpi {
      width: 12%;
      display: flex;
      flex-direction: column;
      align-items: end;

      .analytics-tasks__th {
        text-align: end;
      }
    }

    &__body .analytics-tasks__tr {
      padding: 32px 0;
      border-bottom: 1px solid $light-grey-11;
      cursor: default;

      &:first-child {
        padding-top: 48px;
      }
    }

    &__name-link {
      cursor: pointer;
      color: $base;
      font: $fira-16;
      display: -webkit-box;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      padding-left: 1px;

      &:hover {
        opacity: 0.7;
      }
    }

    &__num-link {
      font: $fira-20-M;

      color: $blue;
      cursor: pointer;

      &:hover {
        opacity: 0.7;
      }
    }

    &__progress-top {
      margin-bottom: 8px;
    }

    &__progress-text {
      font: $fira-20-M;

      &.green {
        color: $approval-text;
      }

      &.yellow {
        color: $yellow;
      }

      &.red {
        color: $red-2;
      }
    }

    &__progress-bar {
      width: 100%;
      max-width: 240px;
    }

    &__problem {
      font: $fira-20-M;
      color: $dark-grey-1;

      &--has {
        color: $red-2;
      }
    }

    &__kpi-val {
      font: $fira-20-M;
      color: $base;

      &.green {
        color: $approval-text;
      }

      &.yellow {
        color: $yellow;
      }

      &.red {
        color: $red-2;
      }
    }

    @media (max-width: $laptop) {
      &__filter {
        margin: 0 -14px 20px;
      }

      &__header {
        display: none;
      }

      &__body .analytics-tasks__tr {
        flex-wrap: wrap;
        padding: 24px 0;
        cursor: default;

        &:first-child {
          padding-top: 24px;
        }
      }

      &__col--name,
      &__col--countries,
      &__col--subtasks,
      &__col--progress,
      &__col--problems,
      &__col--kpi {
        width: 100%;
        padding-right: 0;
        margin-bottom: 8px;
      }

      &__col--problems {
        display: none;
      }
    }
  }

  .modal-task {
    padding: 0 4px;

    &__header {
      font: $fira-20-M;
    }

    &__count {
      font: $fira-16;
      color: $base;
      margin-bottom: 16px;
    }

    &__list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    &__item {
      font: $fira-16;
      color: $base;

      &--link {
        color: $blue;
        cursor: pointer;

        &:hover {
          opacity: 0.7;
        }
      }
    }
  }
</style>

<style lang="scss">
  .g-dialog-content.analytics-tasks-list-modal--layout {
    --content-shadow: 0px 0px 4px 0px #00000080;
    --content-border-radius: 12px;
    box-shadow: var(--content-shadow);
    border-radius: var(--content-border-radius);

    .modal__body {
      position: relative;
      border-radius: 12px;
      overflow: hidden;
      height: 100%;
      padding: 16px;
      display: flex;
      flex-direction: column;
    }

    .modal__body-inner-wrap {
      padding-right: 0;
      margin-right: 0;
    }

    .modal__body-head {
      position: static;
      padding: 0;
      margin: 0 0 32px;

      p:empty {
        display: none;
      }

      .icon {
        position: absolute;
        top: 16px;
        right: 16px;
        z-index: 3;
        width: 16px;
        height: 16px;
        display: block;
        color: #aaaaaa;
        cursor: pointer;

        svg {
          width: 12px;
          height: 12px;
          display: block;
        }
      }
    }
  }
</style>
